import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const DEMO = [
  { role: "Azienda", email: "admin@antincendio.it", password: "Admin123!" },
  { role: "Agente", email: "agente@antincendio.it", password: "Agente123!" },
  { role: "Tecnico", email: "tecnico@antincendio.it", password: "Tecnico123!" },
  { role: "Cliente", email: "cliente@belvedere.it", password: "Cliente123!" },
];

export const landingFor = (role) =>
  role === "tecnico" ? "/agenda" : role === "cliente" ? "/portale" : "/";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(landingFor(u.role));
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block">
        <img
          src="/login-bg.jpg"
          alt="Sistemi antincendio"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-zinc-950/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <Flame className="h-7 w-7 text-primary" strokeWidth={2.5} />
            <span className="font-display font-black text-xl tracking-tighter">FIREGUARD</span>
          </div>
          <div className="max-w-md">
            <p className="overline text-white/60">Vendita · Noleggio · Manutenzione</p>
            <h2 className="font-display text-4xl font-black tracking-tighter leading-none mt-4">
              Il gestionale completo per i sistemi antincendio.
            </h2>
            <p className="text-white/70 mt-5 leading-relaxed">
              Impianti, contratti, scadenze, interventi tecnici e percorsi ottimizzati — in un solo
              posto, dal cantiere all'ufficio.
            </p>
          </div>
          <p className="text-xs text-white/40 font-mono">Collegato a Business Experience</p>
        </div>
      </div>

      <div className="flex items-center px-6 py-14 sm:px-16">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 lg:hidden mb-8">
            <Flame className="h-6 w-6 text-primary" strokeWidth={2.5} />
            <span className="font-display font-black text-lg tracking-tighter">FIREGUARD</span>
          </div>
          <p className="overline">Accesso riservato</p>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-2">Entra nel gestionale</h1>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="overline">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@azienda.it"
                className="h-12 rounded-sm"
                data-testid="login-email-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="overline">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 rounded-sm"
                data-testid="login-password-input"
                required
              />
            </div>
            {err && (
              <p className="text-sm font-medium text-destructive" data-testid="login-error">{err}</p>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-sm font-semibold tracking-wide"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "ACCEDI"}
            </Button>
          </form>

          <div className="mt-10 border-t border-border pt-6">
            <p className="overline mb-3">Accessi demo</p>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.email}
                  type="button"
                  onClick={() => {
                    setEmail(d.email);
                    setPassword(d.password);
                  }}
                  data-testid={`demo-${d.role.toLowerCase()}-btn`}
                  className="text-left border border-border bg-card px-3 py-2.5 rounded-sm hover:border-primary transition-colors duration-200"
                >
                  <span className="block text-sm font-semibold">{d.role}</span>
                  <span className="block text-[11px] text-muted-foreground font-mono truncate">{d.email}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
